<template>
  <v-autocomplete
    v-model="model"
    class="w-full"
    color="primary20"
    label="Buscar Política *"
    theme="light"
    :items="policies"
    item-title="name"
    item-value="id"
    return-object
    density="comfortable"
    :loading="isLoading"
    variant="outlined"
    hide-details
    no-data-text="Nenhuma política encontrada"
    @update:search="onSearch"
    @scroll.passive="onScroll"
  >
    <template #item="{ props, item }">
      <v-list-item v-bind="props">
        <v-list-item-title>{{ item.name }}</v-list-item-title>
        <v-list-item-subtitle>{{ item.company?.name }}</v-list-item-subtitle>
      </v-list-item>
    </template>
  </v-autocomplete>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted } from 'vue'
import Network from '@/Network'
import { useErrorToast } from '@/composables/useErrorToast'

const props = withDefaults(
  defineProps<{
    modelValue?: any
    scheduleId?: number
    isInEffect?: boolean
  }>(),
  {
    isInEffect: false
  }
)

const emit = defineEmits(['update:modelValue'])
const { notifyError } = useErrorToast()

const model = ref(props.modelValue)
const policies = ref<any[]>([])
const isLoading = ref(false)
const searchTerm = ref('')
const offset = ref(0)
const limit = 30
const allLoaded = ref(false)

watch(
  () => props.modelValue,
  (val) => (model.value = val)
)
watch(
  () => model.value,
  (val) => emit('update:modelValue', val)
)

const onSearch = async (search: string) => {
  searchTerm.value = search
  offset.value = 0
  policies.value = []
  allLoaded.value = false
  await fetchDsPolicies()
}

const fetchDsPolicies = async () => {
  if (isLoading.value || allLoaded.value) return

  isLoading.value = true
  try {
    if (!props.scheduleId) {
      console.warn('scheduleId não foi fornecido.')
      return
    }
    const response = await Network.dsPaymentPolicy.listByScheduleId(props.scheduleId, {
      search: searchTerm.value,
      limit: limit,
      offset: offset.value
    })

    const activePolicies = props.isInEffect
      ? response.data.filter((policy) => policy.isActive).filter((policy) => policy.isInEffect)
      : response.data.filter((policy) => policy.isActive)

    const newItems = activePolicies ?? []

    if (newItems.length < limit) allLoaded.value = true

    const existingIds = new Set(policies.value.map((p) => p.id))
    const uniqueItems = newItems.filter((item) => !existingIds.has(item.id))

    policies.value.push(...uniqueItems)
    offset.value += limit
  } catch (error) {
    console.error('Erro ao buscar políticas:', error)
    notifyError(error, 'Não foi possível carregar as políticas de pagamento.')
    // Para a paginação por scroll (cada scroll repetiria o toast); uma nova busca reabre.
    allLoaded.value = true
  } finally {
    isLoading.value = false
  }
}

const onScroll = (event: Event) => {
  const target = event.target as HTMLElement
  if (target.scrollTop + target.clientHeight >= target.scrollHeight - 10) {
    fetchDsPolicies()
  }
}

onMounted(() => {
  fetchDsPolicies()
})
</script>
